import React, { useMemo, useState } from "react";
import {
  Pencil,
  BookOpen,
  FileText,
  Check,
  // BarChart3,
  Wand2,
  LayoutTemplate,
  ChevronDown,
} from "lucide-react";
import "./creatorStudio.css";
import DotGrid from "../../../../components/bitComponents/dotGrid/DotGrid";
import { useContentGeneration } from "../../../../hooks/use-content-generation";
import { BrandDetails } from "../../../../types/add-brand-types";
import {
  ContentFocus,
  CreatorStudioMode,
} from "../../../../types/creator-studio";
import {
  getCreatorStudioTemplateById,
  getCreatorStudioTemplatesForMode,
} from "../../../../constants/creator-studio-templates";

interface Step1Props {
  prompt: string;
  setPrompt: (prompt: string) => void;
  additionalInstructions: string;
  setAdditionalInstructions: (instructions: string) => void;
  regenerationFeedbackHistory: string[];
  onClearRegenerationFeedbackHistory: () => void;
  creatorMode: CreatorStudioMode;
  setCreatorMode: (mode: CreatorStudioMode) => void;
  contentFocus: ContentFocus;
  setContentFocus: (focus: ContentFocus) => void;
  selectedTone: string;
  setSelectedTone: (tone: string) => void;
  selectedTemplateId: string | null;
  setSelectedTemplateId: (templateId: string | null) => void;
  onNext: () => void;
  onSkipToOwnPost?: () => void;
  onUserSelectedKnowledge: (knowledge: string[]) => void;
  brandDetails: BrandDetails;
}

const Step1: React.FC<Step1Props> = ({
  prompt,
  setPrompt,
  additionalInstructions,
  setAdditionalInstructions,
  regenerationFeedbackHistory,
  onClearRegenerationFeedbackHistory,
  creatorMode,
  setCreatorMode,
  contentFocus,
  setContentFocus,
  selectedTone,
  setSelectedTone,
  selectedTemplateId,
  setSelectedTemplateId,
  onNext,
  onSkipToOwnPost,
  brandDetails,
  onUserSelectedKnowledge,
}) => {
  const {
    setActiveTab,
    showToneDropdown,
    showKnowledgeDropdown,
    toneRef,
    knowledgeRef,
    setShowToneDropdown,
    setShowKnowledgeDropdown,
    tones,
    knowledgeFiles,
    selectedKnowledge,
    toggleKnowledge,
  } = useContentGeneration(
    brandDetails,
    onUserSelectedKnowledge,
    contentFocus,
    selectedTone,
    setSelectedTone
  );

  // const handleInspirationClick = (text: string) => {
  //   setPrompt(text);
  // };

  const availableTemplates = useMemo(
    () => getCreatorStudioTemplatesForMode(creatorMode),
    [creatorMode]
  );
  const selectedTemplate = getCreatorStudioTemplateById(selectedTemplateId);
  const [isAdditionalInfoOpen, setIsAdditionalInfoOpen] = useState(true);
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const hasAdditionalInstructions = additionalInstructions.trim().length > 0;
  const templateSummary = selectedTemplate?.label ?? "None";

  return (
    <div className="creator-studio-wrapper">
      <div className="aurora-bg">
        {/* <Aurora
          colorStops={["#7cff67", "#B19EEF", "#5227FF"]}
          blend={1.2}
          amplitude={4}
          speed={0.5}
        /> */}
        <DotGrid
          dotSize={3}
          gap={30}
          baseColor="#5227FF"
          activeColor="#5227FF"
          proximity={120}
          shockRadius={550}
          shockStrength={5}
          resistance={750}
          returnDuration={1.5}
        />
      </div>

      <div className="creator-studio-content">
        <div className="cs-header-section">
          <h1 className="cs-title">Creator Studio</h1>
          <p className="cs-subtitle">
            Transform your ideas into high-performing marketing campaigns with
            our AI engine.
          </p>
        </div>

        <div className="cs-main-card glass-panel">
          <div className="cs-tabs">
            <button
              className={`cs-tab ${
                creatorMode === CreatorStudioMode.SOCIAL_POST ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("social");
                setCreatorMode(CreatorStudioMode.SOCIAL_POST);
              }}
            >
              Social Post
            </button>
            <button
              className={`cs-tab ${
                creatorMode === CreatorStudioMode.BLOG_PROMOTION ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("social");
                setCreatorMode(CreatorStudioMode.BLOG_PROMOTION);
                setContentFocus(ContentFocus.ABOUT_PRODUCT);
              }}
            >
              Promote Blog
            </button>
            {/* <button
              className={`cs-tab ${activeTab === "email" ? "active" : ""}`}
              onClick={() => setActiveTab("email")}
            >
              Email Newsletter
            </button> */}
          </div>

          <div className="cs-input-container">
            <textarea
              className="cs-main-textarea"
              placeholder={
                creatorMode === CreatorStudioMode.BLOG_PROMOTION
                  ? "Paste a blog/article URL or describe the article you want to promote..."
                  : "Start writing or describe your idea..."
              }
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            {/* <button className="cs-magic-enhance-btn">
              <span className="magic-icon">✨</span> Magic Enhance
            </button> */}
          </div>

          {creatorMode === CreatorStudioMode.BLOG_PROMOTION && (
            <div className="cs-blog-mode-note">
              <strong>Blog amplification</strong>
              <span>
                Creates short value-first social drafts with a natural Read
                more link. The post should teach one useful idea before
                sending readers to the full article.
              </span>
            </div>
          )}

          <div className="cs-direction-section">
            <button
              type="button"
              className="cs-direction-summary"
              onClick={() => setIsAdditionalInfoOpen((isOpen) => !isOpen)}
              aria-expanded={isAdditionalInfoOpen}
              aria-controls="creator-additional-instructions-panel"
            >
              <span className="cs-template-summary-icon">
                <Pencil size={18} />
              </span>
              <span className="cs-template-summary-copy">
                <span className="cs-template-label">
                  Additional information
                </span>
                <strong>
                  {hasAdditionalInstructions
                    ? "Custom direction added"
                    : "Optional angle or constraints"}
                </strong>
              </span>
              <span className="cs-template-toggle-text">
                {isAdditionalInfoOpen ? "Hide" : "Show"}
              </span>
              <ChevronDown
                size={18}
                className={`cs-template-chevron ${
                  isAdditionalInfoOpen ? "open" : ""
                }`}
              />
            </button>

            {isAdditionalInfoOpen && (
              <div
                id="creator-additional-instructions-panel"
                className="cs-direction-panel"
              >
                <p>
                  Guide the angle, constraints, or style for this generation.
                </p>
                <textarea
                  id="creator-additional-instructions"
                  className="cs-direction-textarea"
                  placeholder="Example: Focus on why saved brand context matters for small teams. Keep it practical and avoid generic AI hype."
                  value={additionalInstructions}
                  onChange={(e) => setAdditionalInstructions(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="cs-template-section compact">
            <button
              type="button"
              className="cs-template-summary"
              onClick={() => setIsTemplatePickerOpen((isOpen) => !isOpen)}
              aria-expanded={isTemplatePickerOpen}
            >
              <span className="cs-template-summary-icon">
                <LayoutTemplate size={18} />
              </span>
              <span className="cs-template-summary-copy">
                <span className="cs-template-label">Format template</span>
                <strong>{templateSummary}</strong>
              </span>
              {selectedTemplate && (
                <span className="cs-template-selected">
                  {selectedTemplate.platformFit}
                </span>
              )}
              <span className="cs-template-toggle-text">
                {isTemplatePickerOpen
                  ? "Hide"
                  : selectedTemplate
                  ? "Change"
                  : "Choose"}
              </span>
              <ChevronDown
                size={18}
                className={`cs-template-chevron ${
                  isTemplatePickerOpen ? "open" : ""
                }`}
              />
            </button>

            {isTemplatePickerOpen && (
              <div className="cs-template-picker">
                <p>
                  Optional. Use a repeatable structure when you want a more
                  guided draft format.
                </p>
                <div className="cs-template-grid">
                  <button
                    type="button"
                    className={`cs-template-card ${
                      selectedTemplateId == null ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedTemplateId(null);
                      setIsTemplatePickerOpen(false);
                    }}
                  >
                    <span>No template</span>
                    <small>Start from your prompt and brand context only.</small>
                  </button>
                  {availableTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className={`cs-template-card ${
                        selectedTemplateId === template.id ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedTemplateId(template.id);
                        setIsTemplatePickerOpen(false);
                      }}
                    >
                      <span>{template.label}</span>
                      <small>{template.description}</small>
                      <em>{template.platformFit}</em>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {regenerationFeedbackHistory.length > 0 && (
            <div className="cs-feedback-memory">
              <div className="cs-feedback-memory-header">
                <span>Applied feedback memory</span>
                <button
                  type="button"
                  onClick={onClearRegenerationFeedbackHistory}
                >
                  Clear
                </button>
              </div>
              <ol>
                {regenerationFeedbackHistory.map((feedback, index) => (
                  <li key={`${feedback}-${index}`}>{feedback}</li>
                ))}
              </ol>
            </div>
          )}

          {creatorMode === CreatorStudioMode.SOCIAL_POST && (
            <div className="cs-focus-section">
              <div className="cs-focus-header">
                <span className="cs-focus-label">Post Focus</span>
                <span className="cs-focus-help">
                  Choose whether the draft should talk about your product or stay
                  fully general.
                </span>
              </div>
              <div className="cs-focus-toggle">
                <button
                  type="button"
                  className={`cs-focus-option ${
                    contentFocus === ContentFocus.ABOUT_PRODUCT ? "active" : ""
                  }`}
                  onClick={() => setContentFocus(ContentFocus.ABOUT_PRODUCT)}
                >
                  About Product
                </button>
                <button
                  type="button"
                  className={`cs-focus-option ${
                    contentFocus === ContentFocus.GENERAL_POST ? "active" : ""
                  }`}
                  onClick={() => setContentFocus(ContentFocus.GENERAL_POST)}
                >
                  General Post
                </button>
              </div>
            </div>
          )}

          <div className="cs-card-footer">
            <div className="cs-footer-top-row">
              <div className="cs-footer-controls">
                {/* Tone Control */}
                <div className="cs-control-wrapper" ref={toneRef}>
                  <div
                    className={`cs-control-pill interactive ${
                      showToneDropdown ? "active" : ""
                    }`}
                    onClick={() => {
                      setShowToneDropdown(!showToneDropdown);
                      setShowKnowledgeDropdown(false);
                    }}
                  >
                    <span className="icon">
                      <Pencil size={16} />
                    </span>{" "}
                    Tone: {selectedTone}
                  </div>
                  {showToneDropdown && (
                    <div className="cs-dropdown-menu">
                      {tones.map((tone) => (
                        <div
                          key={tone}
                          className={`cs-dropdown-item ${
                            selectedTone === tone ? "selected" : ""
                          }`}
                          onClick={() => {
                            setSelectedTone(tone);
                            setShowToneDropdown(false);
                          }}
                        >
                          {tone}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {contentFocus === ContentFocus.ABOUT_PRODUCT && (
                  <div className="cs-control-wrapper" ref={knowledgeRef}>
                    <div
                      className={`cs-control-pill interactive ${
                        showKnowledgeDropdown ? "active" : ""
                      }`}
                      onClick={() => {
                        setShowKnowledgeDropdown(!showKnowledgeDropdown);
                        setShowToneDropdown(false);
                      }}
                    >
                      <span className="icon">
                        <BookOpen size={16} />
                      </span>{" "}
                      Knowledge: {selectedKnowledge.length} Files Active
                    </div>
                    {showKnowledgeDropdown && (
                      <div className="cs-dropdown-menu wide">
                        <div className="cs-dropdown-header">Active Files</div>
                        {knowledgeFiles.map((file) => (
                          <div
                            key={file}
                            className={`cs-dropdown-item file-item ${
                              selectedKnowledge.includes(file)
                                ? "selected"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleKnowledge(file);
                            }}
                          >
                            <span className="file-icon">
                              <FileText size={16} />
                            </span>
                            <span style={{ flex: 1 }}>{file}</span>
                            {selectedKnowledge.includes(file) && (
                              <Check
                                size={16}
                                style={{ color: "#4f46e5", flexShrink: 0 }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button className="cs-generate-action-btn" onClick={onNext}>
                {creatorMode === CreatorStudioMode.BLOG_PROMOTION
                  ? "Generate Blog Posts"
                  : "Generate Variations"}
                <span className="stack-icon cs-generate-icon">
                  <Wand2 size={18} />
                </span>
              </button>
            </div>
            <div className="cs-footer-or-divider">
              <span className="cs-divider-line" />
              <span className="cs-divider-text">OR</span>
              <span className="cs-divider-line" />
            </div>
            <button
              type="button"
              className="cs-skip-own-post-btn"
              onClick={onSkipToOwnPost ?? onNext}
            >
              Skip AI and Create your own post
            </button>
          </div>
        </div>

        {/* <div className="cs-inspiration-section">
          <h3 className="cs-section-title">Inspiration Rail</h3>
          <div className="cs-inspiration-grid">
            <div
              className="cs-inspiration-card glass-panel"
              onClick={() =>
                handleInspirationClick("Leverage current green initiatives.")
              }
            >
              <div className="cs-insp-icon-box blue">
                <BarChart3 size={20} />
              </div>
              <div className="cs-insp-content">
                <h4>Industry Trend: Sustainability</h4>
                <p>Leverage current green initiatives.</p>
              </div>
            </div>

            <div
              className="cs-inspiration-card glass-panel"
              onClick={() =>
                handleInspirationClick("Condense report into a concise thread.")
              }
            >
              <div className="cs-insp-icon-box purple">
                <FileText size={20} />
              </div>
              <div className="cs-insp-content">
                <h4>Turn Q3 Report into a LinkedIn Thread</h4>
                <p>Condense report into a concise thread.</p>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Step1;
